import { Box } from '@mui/material';
import { lazy, Suspense, useEffect } from 'react';
import type { CohortChartConfig, DataPoint } from '@/features/chartsView';
import { LineChartWrapper, useLineChartParams } from '@/features/chartsView';
import { useDataFetcher } from '@/shared/hooks';
import { useAppSelector } from '@/shared/model';
import { Loader } from '@/shared/ui';
import { CHART_BASE_DIMENSION } from '../../model/constants';
import {
  selectMeta,
  selectSplitDimensionIsMedia,
  selectDimensionIsDate,
} from '../../model/slice';
import type { CohortDataResponse } from '../../model/types';

const LineChart = lazy(() =>
  import('@/features/chartsView').then(({ LineChart }) => {
    return {
      default: LineChart<typeof CHART_BASE_DIMENSION>,
    };
  }),
);

interface Props {
  config: CohortChartConfig;
  onGetData: () => Promise<CohortDataResponse>;
  onDataChange?: (data?: CohortDataResponse) => void;
}

export function Chart({ config, onGetData, onDataChange }: Props) {
  const cohortMeta = useAppSelector(selectMeta);
  const isMediaContent = useAppSelector(selectSplitDimensionIsMedia);
  const isDateContent = useAppSelector(selectDimensionIsDate);
  const { data, dataStatus } = useDataFetcher(onGetData);
  const splitOptions = data?.splitOptions?.[config.splitDimension] || [];
  const {
    noData,
    lines,
    leftY,
    rightY,
    data: chartData,
  } = useLineChartParams({
    meta: cohortMeta,
    chart: config,
    data: (data?.data || []) as DataPoint<typeof CHART_BASE_DIMENSION>[],
    splitOptions,
    defaultSplitOption: CHART_BASE_DIMENSION,
    excludedSplitOptions: config.excludedSplitOptions,
  });

  useEffect(() => {
    onDataChange?.(data);
  }, [data, onDataChange]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LineChartWrapper
        dataStatus={dataStatus}
        noData={noData}
        noSelection={lines.length === config.excludedSplitOptions.length}
      >
        <Suspense fallback={<Loader active />}>
          <LineChart
            sx={{ border: 'none' }}
            syncYAxesDomains={config.syncedYAxes}
            excludedLines={config.excludedSplitOptions}
            isMediaContent={isMediaContent}
            isDateContent={isDateContent}
            xAxisDataKey={CHART_BASE_DIMENSION}
            leftY={leftY}
            rightY={rightY}
            data={chartData}
            lines={lines}
          />
        </Suspense>
      </LineChartWrapper>
    </Box>
  );
}
